import { useStep } from '@/hooks/useStep';
import { useEffect, useMemo, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { decodeUrl } from '@/utils/encodeUrl';

import {
	StepPacient,
	StepProfessionalTable,
	StepScheduleAdmin,
	StepSuccess,
	TimelineLayout,
	TimelineStepProps,
} from '@/components';

interface StepConfig extends TimelineStepProps {
	visible?: boolean;
}

const CreateSession = () => {
	const { currentStep, setTotalStep, setCurrentStep, previousStep } =
		useStep();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [isInitialized, setIsInitialized] = useState(false);

	const stepConfig: Array<StepConfig> = useMemo(
		() => [
			{
				title: 'Pacientes',
				step: 1,
				current: currentStep === 1,
				children: <StepPacient />,
				completed: currentStep > 1,
				visible: true,
			},
			{
				title: 'Profissionais',
				step: 2,
				current: currentStep === 2,
				children: <StepProfessionalTable />,
				completed: currentStep > 2,
				visible: true,
			},
			{
				title: 'Data/Hora',
				step: 3,
				current: currentStep === 3,
				children: <StepScheduleAdmin />,
				completed: currentStep > 3,
				visible: true,
			},
			{
				title: 'Sucesso',
				step: 4,
				current: currentStep === 4,
				children: <StepSuccess />,
				completed: currentStep === 4,
				isLast: true,
				visible: false,
			},
		],
		[currentStep],
	);

	const visualSteps = useMemo(() => {
		return stepConfig
			.filter((step) => step.visible)
			.map((step, index, arr) => ({
				...step,
				step: index + 1,
				isLast: index === arr.length - 1,
			}));
	}, [stepConfig]);

	const currentStepComponent = useMemo(() => {
		return stepConfig.find((step) => step.current)?.children;
	}, [stepConfig]);

	useEffect(() => {
		setTotalStep(stepConfig.length);
		const stepParam = searchParams.get('d');
		const decodedParams = stepParam
			? decodeUrl<{ s: string }>(stepParam)
			: { s: '1' };
		const parsedStep = Number.parseInt(decodedParams.s ?? '1', 10);
		const safeStep = Number.isNaN(parsedStep)
			? 1
			: Math.min(Math.max(parsedStep, 1), stepConfig.length);
		setCurrentStep(safeStep);
		setIsInitialized(true);
	}, []);

	if (!isInitialized) return null;

	const handleBack = () => {
		if (currentStep === 1) {
			navigate('/admin/consultas');
		} else {
			previousStep();
		}
	};

	return (
		<section className="flex min-h-[calc(100vh-4rem)] px-4 pb-4">
			<TimelineLayout stepList={visualSteps} onBack={handleBack}>
				{currentStepComponent}
			</TimelineLayout>
		</section>
	);
};

export { CreateSession };
